import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Check, Paperclip } from 'lucide-react';
import Navbar from './Navbar';
import Footer from './Footer';

// ---------- Job data ----------
const jobsData = {
  'senior-ai-engineer': {
    id: 'senior-ai-engineer',
    title: 'Senior AI Engineer',
    department: 'AI Engineering',
    location: 'Lisbon (Hybrid)',
    type: 'Full-time',
    level: 'Senior',
    description:
      'Lead the development of our AI models for clinical note contextualization, optimize LLM performance, and build robust ML pipelines for on-prem hospital deployment.',
    postedDate: '2024-11-01',
    fullDescription: `
      <h3>About BloodFlow</h3>
      <p>BloodFlow is an on-premise clinical intelligence layer that makes the patient record computable — turning a hospital's EMR from storage into a continuously computed, fully cited clinical state. Deterministic, regulated, inside the EMR. We're finalizing <strong>MDR Class IIa</strong> and preparing for <strong>CE mark</strong>.</p>
      <h3>Role summary</h3>
      <p>As Senior AI Engineer, you lead the development of the core AI models behind the clinical computation layer. You work directly with our research team and clinical advisors to build, optimize, and deploy systems that help physicians make safer diagnostic decisions.</p>
      <h3>Responsibilities</h3>
      <h4>AI model development</h4>
      <ul>
        <li>Design and implement advanced AI models for medical data interpretation</li>
        <li>Optimize <strong>LLM performance</strong> for clinical accuracy and reliability</li>
        <li>Build and maintain <strong>RAG pipelines</strong> using LangChain, LlamaIndex, and vector databases</li>
        <li>Develop custom fine-tuning strategies for domain-specific medical knowledge</li>
      </ul>
      <h4>Research & innovation</h4>
      <ul>
        <li>Research state-of-the-art techniques in medical AI and natural language processing</li>
        <li>Collaborate with clinical experts to understand medical reasoning patterns</li>
        <li>Implement novel approaches for handling medical uncertainty and edge cases</li>
      </ul>
      <h4>Production & deployment</h4>
      <ul>
        <li>Deploy AI models in on-prem environments with high reliability standards</li>
        <li>Implement comprehensive model monitoring and performance tracking</li>
        <li>Ensure models meet regulatory requirements for medical device certification</li>
      </ul>
      <h3>What we're looking for</h3>
      <h4>Must-have</h4>
      <ul>
        <li>5+ years experience in AI/ML engineering with production deployments</li>
        <li>Strong expertise in <strong>LLMs</strong>, <strong>transformers</strong>, and <strong>deep learning</strong></li>
        <li>Experience with <strong>RAG systems</strong>, <strong>vector databases</strong>, and <strong>embedding models</strong></li>
        <li>Proficiency in Python, PyTorch/TensorFlow, and modern ML frameworks</li>
      </ul>
      <h4>Nice-to-have</h4>
      <ul>
        <li>PhD or Master's in AI/ML, Computer Science, or related field</li>
        <li>Experience in <strong>healthcare AI</strong>, <strong>medical NLP</strong>, or <strong>clinical decision support</strong></li>
        <li>Knowledge of regulatory compliance in healthcare (MDR, FDA, CE marking)</li>
      </ul>
    `,
    benefits: [
      'Lead AI development at a high-impact healthcare startup',
      'Work with cutting-edge AI technology and real medical data',
      'Direct collaboration with clinical experts and researchers',
      'Competitive salary with equity participation',
      'Conference attendance and research publication support',
      'Flexible hybrid work (Lisbon)',
    ],
  },
  'ml-ops-engineer': {
    id: 'ml-ops-engineer',
    title: 'MLOps Engineer',
    department: 'AI Engineering',
    location: 'Lisbon (Hybrid)',
    type: 'Full-time',
    level: 'Mid',
    description:
      'Build and maintain the ML infrastructure that ships the clinical computation layer inside hospital networks. Deterministic pipelines, auditable deployments.',
    postedDate: '2024-10-28',
    fullDescription: `
      <h3>About BloodFlow</h3>
      <p>BloodFlow is an on-premise clinical intelligence layer that makes the patient record computable — turning a hospital's EMR from storage into a continuously computed, fully cited clinical state. Deterministic, regulated, inside the EMR.</p>
      <h3>Role summary</h3>
      <p>As MLOps Engineer, you build and maintain the infrastructure that powers our AI models in production. You ensure our systems are reliable, scalable, and meet the high standards required for healthcare applications.</p>
      <h3>Responsibilities</h3>
      <h4>Infrastructure & deployment</h4>
      <ul>
        <li>Build and maintain ML infrastructure using Kubernetes, Docker, and cloud services</li>
        <li>Implement CI/CD pipelines for AI model deployment and updates</li>
        <li>Design scalable architectures for both cloud and on-premise deployments</li>
        <li>Manage model versioning, rollbacks, and A/B testing infrastructure</li>
      </ul>
      <h4>Monitoring & observability</h4>
      <ul>
        <li>Implement comprehensive monitoring for model performance and drift detection</li>
        <li>Build dashboards and alerting systems for production AI systems</li>
        <li>Ensure compliance with healthcare audit and traceability requirements</li>
      </ul>
      <h4>Security & compliance</h4>
      <ul>
        <li>Implement security best practices for AI model deployment</li>
        <li>Ensure GDPR compliance and data privacy in ML pipelines</li>
        <li>Support regulatory requirements for medical device certification</li>
      </ul>
      <h3>What we're looking for</h3>
      <h4>Must-have</h4>
      <ul>
        <li>3+ years experience in DevOps, MLOps, or production ML systems</li>
        <li>Strong expertise in <strong>Kubernetes</strong>, <strong>Docker</strong>, and <strong>cloud platforms</strong></li>
        <li>Experience with <strong>CI/CD pipelines</strong>, <strong>monitoring tools</strong>, and <strong>infrastructure as code</strong></li>
        <li>Proficiency in Python and familiarity with ML frameworks</li>
      </ul>
      <h4>Nice-to-have</h4>
      <ul>
        <li>Experience in <strong>healthcare technology</strong> or <strong>regulated environments</strong></li>
        <li>Knowledge of MLOps tools like MLflow, Kubeflow, or similar platforms</li>
        <li>Background in site reliability engineering (SRE)</li>
      </ul>
    `,
    benefits: [
      'Build critical infrastructure for healthcare AI',
      'Work with modern MLOps tools and cloud technologies',
      'Impact on patient care through reliable AI systems',
      'Competitive salary with equity participation',
      'Professional development and training opportunities',
      'Flexible hybrid work (Lisbon)',
    ],
  },
  'ai-research-scientist': {
    id: 'ai-research-scientist',
    title: 'AI Research Scientist',
    department: 'AI Engineering',
    location: 'Lisbon (Hybrid)',
    type: 'Full-time',
    level: 'Senior',
    description:
      'Research and develop cutting-edge approaches for medical reasoning and contextualization. Publish findings and collaborate with clinical advisors.',
    postedDate: '2024-10-25',
    fullDescription: `
      <h3>About BloodFlow</h3>
      <p>BloodFlow is an on-premise clinical intelligence layer that makes the patient record computable — turning a hospital's EMR from storage into a continuously computed, fully cited clinical state. Deterministic, regulated, inside the EMR.</p>
      <h3>Role summary</h3>
      <p>As AI Research Scientist, you research and develop cutting-edge AI techniques for medical data analysis. You work at the intersection of AI research and clinical practice, collaborating with medical experts to solve complex healthcare challenges.</p>
      <h3>Responsibilities</h3>
      <h4>Research & innovation</h4>
      <ul>
        <li>Research novel AI techniques for medical data interpretation and clinical reasoning</li>
        <li>Develop new approaches for handling medical uncertainty and multi-modal data</li>
        <li>Investigate methods for improving AI explainability in clinical contexts</li>
      </ul>
      <h4>Publications & knowledge sharing</h4>
      <ul>
        <li>Publish research findings in top-tier AI and medical informatics conferences</li>
        <li>Present work at academic conferences and industry events</li>
        <li>Collaborate with academic institutions and research partners</li>
      </ul>
      <h4>Clinical collaboration</h4>
      <ul>
        <li>Work closely with clinical advisors to understand medical reasoning patterns</li>
        <li>Design studies to validate AI approaches with real clinical data</li>
        <li>Translate clinical requirements into technical research problems</li>
      </ul>
      <h3>What we're looking for</h3>
      <h4>Must-have</h4>
      <ul>
        <li>PhD in AI/ML, Computer Science, or related field with strong research track record</li>
        <li>Publications in top-tier AI/ML conferences (NeurIPS, ICML, ICLR, AAAI, etc.)</li>
        <li>Deep expertise in <strong>machine learning</strong>, <strong>NLP</strong>, or <strong>deep learning</strong></li>
        <li>Experience with large language models and transformer architectures</li>
      </ul>
      <h4>Nice-to-have</h4>
      <ul>
        <li>Experience in <strong>medical AI</strong>, <strong>healthcare informatics</strong>, or <strong>clinical NLP</strong></li>
        <li>Knowledge of medical terminology, clinical workflows, or diagnostic processes</li>
        <li>Background in uncertainty quantification or explainable AI</li>
      </ul>
    `,
    benefits: [
      'Lead cutting-edge research in healthcare AI',
      'Publish in top-tier conferences with company support',
      'Collaborate with world-class clinical experts',
      'Competitive salary with equity participation',
      'Conference travel and research publication funding',
      'Flexible schedule for research and academic collaboration',
    ],
  },
  'people-operations-manager': {
    id: 'people-operations-manager',
    title: 'People Operations Manager',
    department: 'Human Resources',
    location: 'Lisbon (Hybrid)',
    type: 'Full-time',
    level: 'Mid / Senior',
    description:
      'Shape the BloodFlow culture, lead talent acquisition, manage employee experience, and build scalable HR processes as we grow.',
    postedDate: '2024-10-30',
    fullDescription: `
      <h3>About BloodFlow</h3>
      <p>BloodFlow is an on-premise clinical intelligence layer that makes the patient record computable — turning a hospital's EMR from storage into a continuously computed, fully cited clinical state. Deterministic, regulated, inside the EMR.</p>
      <h3>Role summary</h3>
      <p>As People Operations Manager, you shape the BloodFlow culture and employee experience as we scale from startup to scale-up. You lead talent acquisition, build HR processes, and ensure we maintain our mission-driven culture while growing rapidly.</p>
      <h3>Responsibilities</h3>
      <h4>Talent acquisition</h4>
      <ul>
        <li>Lead end-to-end recruitment for technical and non-technical roles</li>
        <li>Source candidates through multiple channels and build talent pipelines</li>
        <li>Partner with hiring managers to define role requirements and interview processes</li>
        <li>Build and maintain our employer brand across various platforms</li>
      </ul>
      <h4>Employee experience & culture</h4>
      <ul>
        <li>Design and implement onboarding processes for new team members</li>
        <li>Develop employee engagement initiatives and feedback mechanisms</li>
        <li>Manage performance review processes and career development programs</li>
        <li>Foster a diverse, inclusive, and high-performance culture</li>
      </ul>
      <h4>HR operations & compliance</h4>
      <ul>
        <li>Build scalable HR processes and policies as we grow</li>
        <li>Ensure compliance with Portuguese and EU employment law</li>
        <li>Manage compensation and benefits programs</li>
      </ul>
      <h3>What we're looking for</h3>
      <h4>Must-have</h4>
      <ul>
        <li>3+ years experience in HR/People Operations, preferably at startups or scale-ups</li>
        <li>Strong experience in <strong>talent acquisition</strong> and <strong>full-cycle recruiting</strong></li>
        <li>Knowledge of Portuguese employment law and EU regulations</li>
        <li>Fluency in English and Portuguese</li>
      </ul>
      <h4>Nice-to-have</h4>
      <ul>
        <li>Experience recruiting for <strong>AI/ML</strong> or <strong>technical roles</strong></li>
        <li>Background in <strong>healthcare</strong> or <strong>regulated industries</strong></li>
        <li>Experience scaling teams from 10 to 50+ employees</li>
      </ul>
    `,
    benefits: [
      'Shape company culture at a high-growth healthcare startup',
      'Build HR function from the ground up',
      'Work with a diverse, international team',
      'Competitive salary with equity participation',
      'Professional development and HR certification support',
      'Flexible hybrid work (Lisbon)',
    ],
  },
  'talent-acquisition-specialist': {
    id: 'talent-acquisition-specialist',
    title: 'Talent Acquisition Specialist',
    department: 'Human Resources',
    location: 'Lisbon (Hybrid)',
    type: 'Full-time',
    level: 'Mid',
    description:
      'Source and recruit top talent across engineering, AI, and clinical. Build our employer brand, own the recruitment lifecycle end-to-end.',
    postedDate: '2024-10-26',
    fullDescription: `
      <h3>About BloodFlow</h3>
      <p>BloodFlow is an on-premise clinical intelligence layer that makes the patient record computable — turning a hospital's EMR from storage into a continuously computed, fully cited clinical state. Deterministic, regulated, inside the EMR.</p>
      <h3>Role summary</h3>
      <p>As Talent Acquisition Specialist, you source and recruit top talent across our engineering, AI, and clinical teams. You play a crucial role in building our team as we scale rapidly.</p>
      <h3>Responsibilities</h3>
      <h4>Sourcing & recruiting</h4>
      <ul>
        <li>Source candidates through LinkedIn, GitHub, conferences, and technical communities</li>
        <li>Manage full recruitment lifecycle from initial outreach to offer negotiation</li>
        <li>Build talent pipelines for hard-to-fill technical roles</li>
        <li>Conduct initial candidate screening and qualification calls</li>
      </ul>
      <h4>Employer branding</h4>
      <ul>
        <li>Build and maintain our presence on job boards and professional networks</li>
        <li>Create compelling job descriptions and recruitment content</li>
        <li>Represent BloodFlow at career fairs, meetups, and industry events</li>
      </ul>
      <h3>What we're looking for</h3>
      <h4>Must-have</h4>
      <ul>
        <li>2+ years experience in talent acquisition or technical recruiting</li>
        <li>Experience recruiting for <strong>engineering</strong> and <strong>technical roles</strong></li>
        <li>Strong sourcing skills using LinkedIn, Boolean search, and various platforms</li>
        <li>Fluency in English (Portuguese is a plus)</li>
      </ul>
      <h4>Nice-to-have</h4>
      <ul>
        <li>Experience recruiting for <strong>AI/ML</strong> or <strong>data science</strong> roles</li>
        <li>Background in <strong>startup</strong> or <strong>scale-up</strong> environments</li>
        <li>Knowledge of European talent markets</li>
      </ul>
    `,
    benefits: [
      'Build a talent pipeline for a cutting-edge healthcare AI company',
      'Work with a diverse, international candidate pool',
      'Impact on company growth through strategic hiring',
      'Competitive salary with performance bonuses',
      'Professional development and recruiting certification support',
      'Flexible hybrid work (Lisbon)',
    ],
  },
  'junior-ai-engineer': {
    id: 'junior-ai-engineer',
    title: 'Junior AI Engineer',
    department: 'AI Engineering',
    location: 'Lisbon (Hybrid)',
    type: 'Full-time',
    level: 'Junior',
    description:
      'Support AI model development, work on data preprocessing pipelines, and contribute to our medical AI research under senior guidance.',
    postedDate: '2024-10-24',
    fullDescription: `
      <h3>About BloodFlow</h3>
      <p>BloodFlow is an on-premise clinical intelligence layer that makes the patient record computable — turning a hospital's EMR from storage into a continuously computed, fully cited clinical state. Deterministic, regulated, inside the EMR.</p>
      <h3>Role summary</h3>
      <p>As Junior AI Engineer, you support our AI development team in building and improving our medical AI models. You work on data preprocessing, model training, and evaluation under the guidance of senior engineers and researchers.</p>
      <h3>Responsibilities</h3>
      <h4>AI development support</h4>
      <ul>
        <li>Assist in training and fine-tuning AI models for medical data analysis</li>
        <li>Implement data preprocessing and feature engineering pipelines</li>
        <li>Support model evaluation and performance testing</li>
      </ul>
      <h4>Data & experimentation</h4>
      <ul>
        <li>Work with medical datasets and clinical annotations</li>
        <li>Support A/B testing and model comparison experiments</li>
        <li>Help with data quality assessment and validation</li>
      </ul>
      <h3>What we're looking for</h3>
      <h4>Must-have</h4>
      <ul>
        <li>Bachelor's or Master's degree in Computer Science, AI/ML, or related field</li>
        <li>Basic experience with <strong>machine learning</strong> and <strong>Python programming</strong></li>
        <li>Familiarity with ML frameworks (PyTorch, TensorFlow, or scikit-learn)</li>
        <li>Strong problem-solving skills and eagerness to learn</li>
      </ul>
      <h4>Nice-to-have</h4>
      <ul>
        <li>Experience with <strong>natural language processing</strong> or <strong>large language models</strong></li>
        <li>Knowledge of medical terminology or healthcare domain</li>
        <li>Familiarity with version control (Git) and software development practices</li>
      </ul>
    `,
    benefits: [
      'Launch your AI career in healthcare with mentorship from senior engineers',
      'Work on cutting-edge medical AI technology',
      'Learn from world-class AI researchers and engineers',
      'Competitive salary with equity participation',
      'Professional development and training opportunities',
      'Flexible hybrid work (Lisbon)',
    ],
  },
};

// ---------- Page ----------
const JobDetailsPage = () => {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const applyRef = useRef(null);

  useEffect(() => {
    setTimeout(() => {
      setJob(jobsData[id] || null);
      setLoading(false);
    }, 150);
  }, [id]);

  return (
    <div className="bf-page">
      <Navbar />
      <div style={{ height: 96 }} />

      {loading ? (
        <section className="bf-section">
          <div className="bf-container">
            <div className="bf-small">Loading position…</div>
          </div>
        </section>
      ) : !job ? (
        <NotFound />
      ) : (
        <JobView job={job} applyRef={applyRef} />
      )}

      <Footer />

      <style>{`
        .bf-job-description h3 {
          font-family: var(--bf-serif);
          font-size: clamp(22px, 2.2vw, 28px);
          letter-spacing: -0.015em;
          color: var(--bf-ink);
          margin: 40px 0 14px;
          padding-top: 24px;
          border-top: 1px solid var(--bf-rule);
        }
        .bf-job-description h3:first-child {
          margin-top: 0;
          padding-top: 0;
          border-top: none;
        }
        .bf-job-description h4 {
          font-family: var(--bf-mono);
          font-size: 11px;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: var(--bf-accent);
          margin: 28px 0 12px;
          font-weight: 500;
        }
        .bf-job-description p {
          font-family: var(--bf-sans);
          font-size: 16px;
          line-height: 1.6;
          color: var(--bf-ink-2);
          margin-bottom: 16px;
          max-width: 62ch;
        }
        .bf-job-description ul {
          list-style: none;
          padding: 0;
          margin: 0 0 20px;
          max-width: 62ch;
        }
        .bf-job-description li {
          position: relative;
          padding: 8px 0 8px 20px;
          font-family: var(--bf-sans);
          font-size: 15px;
          line-height: 1.5;
          color: var(--bf-ink-2);
          border-bottom: 1px dashed var(--bf-rule);
        }
        .bf-job-description li:last-child { border-bottom: none; }
        .bf-job-description li::before {
          content: '—';
          position: absolute;
          left: 0;
          top: 8px;
          color: var(--bf-accent);
        }
        .bf-job-description strong {
          color: var(--bf-ink);
          font-weight: 600;
        }

        @media (max-width: 980px) {
          .bf-job-grid { grid-template-columns: 1fr !important; gap: 48px !important; }
          .bf-job-sidebar { position: static !important; }
        }
      `}</style>
    </div>
  );
};

// ---------- Job view ----------
const JobView = ({ job, applyRef }) => (
  <>
    <section className="bf-section" style={{ paddingTop: 56, paddingBottom: 40 }}>
      <div className="bf-container">
        <Link
          to="/careers"
          className="bf-small"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            color: 'var(--bf-muted)',
            textDecoration: 'none',
            marginBottom: 32,
          }}
        >
          <ArrowLeft size={14} /> All careers
        </Link>

        <div className="bf-section-head" style={{ marginBottom: 16 }}>
          <span className="bf-eyebrow"><span className="dot"></span>{job.department}</span>
          <div>
            <h2 style={{ fontSize: 'clamp(32px, 4.4vw, 60px)' }}>{job.title}</h2>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 8 }}>
          <span className="bf-tag bf-tag-accent">{job.level}</span>
          <span className="bf-tag">{job.type}</span>
          <span className="bf-tag">{job.location}</span>
        </div>

        <p className="bf-lead" style={{ marginTop: 28, color: 'var(--bf-ink-2)', maxWidth: '62ch' }}>
          {job.description}
        </p>
      </div>
    </section>

    <section className="bf-section" style={{ paddingTop: 16, paddingBottom: 96 }}>
      <div className="bf-container">
        <div
          className="bf-job-grid"
          style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 80, alignItems: 'start' }}
        >
          {/* Content */}
          <div>
            <div
              className="bf-job-description"
              dangerouslySetInnerHTML={{ __html: job.fullDescription }}
            />
          </div>

          {/* Sidebar */}
          <aside
            className="bf-job-sidebar"
            style={{ position: 'sticky', top: 132, display: 'flex', flexDirection: 'column', gap: 24 }}
          >
            <div
              style={{
                background: 'var(--bf-surface)',
                border: '1px solid var(--bf-rule-2)',
                borderRadius: 16,
                padding: 28,
                boxShadow: '0 32px 70px -30px rgba(0,0,0,0.7), inset 0 1px 0 0 rgba(255,255,255,0.04)',
              }}
            >
              <div className="bf-small">Apply</div>
              <h3 style={{ marginTop: 6, fontSize: 22 }}>Ready to join?</h3>
              <p
                className="bf-body-p"
                style={{ marginTop: 8, fontSize: 14, color: 'var(--bf-muted)' }}
              >
                Submit your application below. We review every submission and respond within a week.
              </p>
              <button
                onClick={() => applyRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                className="bf-btn bf-btn-primary bf-btn-lg"
                style={{ marginTop: 16, width: '100%', justifyContent: 'center' }}
              >
                Apply now <span className="bf-arrow">→</span>
              </button>
              <div className="bf-small" style={{ marginTop: 16, color: 'var(--bf-muted-2)' }}>
                Posted{' '}
                {new Date(job.postedDate).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })}
              </div>
            </div>

            <div style={{ paddingTop: 24, marginTop: 4, borderTop: '1px solid var(--bf-rule-2)' }}>
              <div className="bf-small">What you'll get</div>
              <ul
                style={{
                  listStyle: 'none',
                  padding: 0,
                  marginTop: 16,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                }}
              >
                {job.benefits.map((b, i) => (
                  <li
                    key={i}
                    style={{
                      fontFamily: 'var(--bf-sans)',
                      fontSize: 14,
                      color: 'var(--bf-ink-2)',
                      lineHeight: 1.5,
                      paddingLeft: 20,
                      position: 'relative',
                    }}
                  >
                    <Check
                      size={14}
                      style={{ position: 'absolute', left: 0, top: 4, color: 'var(--bf-accent)' }}
                    />
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>

        {/* Apply form */}
        <div ref={applyRef}>
          <ApplyForm job={job} />
        </div>
      </div>
    </section>
  </>
);

// ---------- Apply form ----------
const ApplyForm = ({ job }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    coverLetter: '',
    resume: null,
  });
  const [status, setStatus] = useState({ submitting: false, success: false, error: null });
  const BASE_URL = process.env.REACT_APP_BASE_URL;

  const onChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const onFile = (e) => {
    const file = e.target.files[0];
    if (file && file.size > 5 * 1024 * 1024) {
      alert('File is too large. Please upload a file smaller than 5 MB.');
      e.target.value = '';
      return;
    }
    setFormData((p) => ({ ...p, resume: file }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus({ submitting: true, success: false, error: null });
    try {
      if (!formData.resume) throw new Error('Please attach your resume/CV.');
      const payload = new FormData();
      payload.append('job_title', job.title);
      payload.append('job_id', job.id);
      payload.append('name', formData.name);
      payload.append('email', formData.email);
      payload.append('phone', formData.phone);
      payload.append('cover_letter', formData.coverLetter);
      payload.append('resume', formData.resume);
      await axios.post(`${BASE_URL}/send-job-application`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setStatus({ submitting: false, success: true, error: null });
      setFormData({ name: '', email: '', phone: '', coverLetter: '', resume: null });
    } catch (err) {
      setStatus({
        submitting: false,
        success: false,
        error: err.response?.data?.message || err.message || 'Submission failed. Please try again.',
      });
    }
  };

  return (
    <div style={{ marginTop: 80, paddingTop: 64, borderTop: '1px solid var(--bf-rule-2)' }}>
      <div className="bf-section-head" style={{ marginBottom: 40 }}>
        <span className="bf-eyebrow"><span className="dot"></span>Application</span>
        <div>
          <h2>
            Apply for <span className="bf-grad-text">{job.title}</span>
          </h2>
        </div>
      </div>

      {status.success ? (
        <div
          style={{
            padding: '56px 40px',
            background: 'var(--bf-surface)',
            border: '1px solid var(--bf-rule-2)',
            borderRadius: 16,
            textAlign: 'center',
            boxShadow: '0 32px 70px -30px rgba(0,0,0,0.7), inset 0 1px 0 0 rgba(255,255,255,0.04)',
          }}
        >
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              background: 'var(--bf-signal-bg)',
              border: '1px solid color-mix(in oklab, var(--bf-accent) 35%, transparent)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
            }}
          >
            <Check size={26} style={{ color: 'var(--bf-accent)' }} />
          </div>
          <h3>Application submitted.</h3>
          <p
            className="bf-body-p"
            style={{ marginTop: 12, maxWidth: '44ch', margin: '12px auto 0', color: 'var(--bf-muted)' }}
          >
            Thank you for applying for the <strong style={{ color: 'var(--bf-ink)' }}>{job.title}</strong> role. We'll
            review your application and get back to you within 5–7 business days.
          </p>
        </div>
      ) : (
        <form
          onSubmit={onSubmit}
          style={{
            background: 'var(--bf-surface)',
            border: '1px solid var(--bf-rule-2)',
            borderRadius: 16,
            padding: '40px 44px',
            boxShadow: '0 32px 70px -30px rgba(0,0,0,0.7), inset 0 1px 0 0 rgba(255,255,255,0.04)',
          }}
        >
          {status.error && (
            <div
              style={{
                marginBottom: 24,
                padding: '12px 16px',
                background: 'var(--bf-signal-bg)',
                borderLeft: '3px solid var(--bf-accent)',
                fontFamily: 'var(--bf-sans)',
                fontSize: 14,
                color: 'var(--bf-accent-2)',
              }}
            >
              {status.error}
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
            <FormRow>
              <Field label="Full name" required>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={onChange}
                  required
                  placeholder="Your full name"
                  className="bf-apply-input"
                />
              </Field>
              <Field label="Email" required>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={onChange}
                  required
                  placeholder="you@email.com"
                  className="bf-apply-input"
                />
              </Field>
            </FormRow>

            <FormRow>
              <Field label="Phone">
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={onChange}
                  placeholder="+351 …"
                  className="bf-apply-input"
                />
              </Field>
              <Field label="Resume / CV" required>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '10px 0',
                    borderBottom: '1px solid var(--bf-rule-2)',
                    cursor: 'pointer',
                    fontFamily: 'var(--bf-sans)',
                    fontSize: 14.5,
                    color: formData.resume ? 'var(--bf-ink)' : 'var(--bf-muted-2)',
                  }}
                >
                  <Paperclip size={14} style={{ color: 'var(--bf-accent)' }} />
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {formData.resume ? formData.resume.name : 'Attach PDF, DOC, DOCX · max 5 MB'}
                  </span>
                  <input
                    type="file"
                    name="resume"
                    required
                    accept=".pdf,.doc,.docx"
                    onChange={onFile}
                    style={{ display: 'none' }}
                  />
                </label>
              </Field>
            </FormRow>

            <Field label="Cover letter">
              <textarea
                name="coverLetter"
                rows={6}
                value={formData.coverLetter}
                onChange={onChange}
                placeholder="Tell us why this role fits and what you'd want to build here."
                className="bf-apply-input"
                style={{ resize: 'vertical' }}
              />
            </Field>
          </div>

          <div style={{ marginTop: 32, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
            <p
              className="bf-small"
              style={{
                margin: 0,
                color: 'var(--bf-muted-2)',
                textTransform: 'none',
                letterSpacing: '0.02em',
                maxWidth: '36ch',
              }}
            >
              By submitting, you agree to our privacy policy and terms of service.
            </p>
            <button
              type="submit"
              disabled={status.submitting}
              className="bf-btn bf-btn-primary bf-btn-lg"
              style={{
                opacity: status.submitting ? 0.7 : 1,
                cursor: status.submitting ? 'not-allowed' : 'pointer',
              }}
            >
              {status.submitting ? 'Submitting…' : 'Submit application'}{' '}
              {!status.submitting && <span className="bf-arrow">→</span>}
            </button>
          </div>
        </form>
      )}

      <style>{`
        .bf-apply-input {
          width: 100%;
          font-family: var(--bf-sans);
          font-size: 15px;
          color: var(--bf-ink);
          background: transparent;
          border: none;
          border-bottom: 1px solid var(--bf-rule-2);
          padding: 10px 0;
          outline: none;
          transition: border-color 0.15s;
        }
        .bf-apply-input::placeholder { color: var(--bf-muted-2); }
        .bf-apply-input:focus { border-bottom-color: var(--bf-accent); }
        textarea.bf-apply-input { background: rgba(255,255,255,0.02); }

        .bf-apply-row { display: grid; grid-template-columns: 1fr 1fr; gap: 22px; }
        @media (max-width: 640px) {
          .bf-apply-row { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
};

const Field = ({ label, required, children }) => (
  <label style={{ display: 'block' }}>
    <div className="bf-caption" style={{ marginBottom: 4, color: 'var(--bf-muted)' }}>
      {label}
      {required && <span style={{ color: 'var(--bf-accent)', marginLeft: 4 }}>*</span>}
    </div>
    {children}
  </label>
);

const FormRow = ({ children }) => <div className="bf-apply-row">{children}</div>;

const NotFound = () => (
  <section className="bf-section">
    <div className="bf-container" style={{ textAlign: 'center', padding: '80px 0' }}>
      <span className="bf-eyebrow"><span className="dot"></span>404</span>
      <h2 style={{ marginTop: 20 }}>Position not found.</h2>
      <p className="bf-body-p" style={{ marginTop: 16, maxWidth: '44ch', margin: '16px auto 32px' }}>
        The position you're looking for doesn't exist, has been filled, or was removed. Check out our other open roles.
      </p>
      <Link to="/careers" className="bf-btn bf-btn-primary bf-btn-lg">
        Browse open roles <span className="bf-arrow">→</span>
      </Link>
    </div>
  </section>
);

export default JobDetailsPage;
